import React, { useState, useEffect } from 'react';
import {
  MessageCircle, Save, Loader2, CheckCircle2, AlertTriangle, ShieldCheck,
  Send, RefreshCw, Eye, EyeOff, Plus, X, Smartphone, Activity, Beaker, Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  enabled: false, engine: 'openwa', mode: 'dev', dev_strategy: 'redirect',
  test_numbers: [], base_url: '', api_key: '', session_id: '', session_name: '',
  sender_number: '', webhook_secret: '',
  rate: { per_hour: 25, min_delay_s: 3, max_delay_s: 8, window_start_h: 7, window_end_h: 20, tz: 'Africa/Abidjan' },
  warmup: { active: false, daily_cap: 20, growth_pct: 50 },
};

export default function OpenWaConfigPanel() {
  const [cfg, setCfg] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [session, setSession] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [outbox, setOutbox] = useState({ items: [], counts: {} });
  const [confirmProd, setConfirmProd] = useState(false);
  const [registeringWh, setRegisteringWh] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` } });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/system-config/openwa`, authHeader());
      setCfg({ ...EMPTY, ...data, rate: { ...EMPTY.rate, ...(data.rate || {}) }, warmup: { ...EMPTY.warmup, ...(data.warmup || {}) } });
    } catch (e) { toast.error('Erreur chargement config OpenWA'); }
    setLoading(false);
  };

  const loadSession = async () => {
    try { const { data } = await axios.get(`${API}/notifications/whatsapp/session`, authHeader()); setSession(data); }
    catch (e) { setSession(null); }
  };
  const loadOutbox = async () => {
    try { const { data } = await axios.get(`${API}/notifications/whatsapp/outbox`, authHeader()); setOutbox(data); }
    catch (e) {}
  };

  useEffect(() => { load(); loadSession(); loadOutbox(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      await axios.post(`${API}/system-config/openwa`, next, authHeader());
      toast.success('Configuration enregistrée');
      await load(); await loadSession();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur enregistrement'); }
    setSaving(false);
  };

  const save = () => persist(cfg);

  const addNumber = () => {
    const n = (newNumber || '').replace(/\D/g, '');
    if (!n) return;
    if (cfg.test_numbers.includes(n)) { toast.info('Numéro déjà présent'); return; }
    setCfg({ ...cfg, test_numbers: [...cfg.test_numbers, n] });
    setNewNumber('');
  };
  const removeNumber = (n) => setCfg({ ...cfg, test_numbers: cfg.test_numbers.filter((x) => x !== n) });

  const sendTest = async () => {
    const p = (testPhone || '').replace(/\D/g, '');
    if (!p) { toast.error('Saisissez un numéro de test'); return; }
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/notifications/whatsapp/test`, { phone: p, message: '✅ Test Baobab Connect — passerelle WhatsApp OpenWA' }, authHeader());
      if (data.success) toast.success('Message de test envoyé ✅'); else toast.error(data.error || 'Échec envoi');
      loadOutbox();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec envoi test'); }
    setSendingTest(false);
  };

  const switchToProduction = () => { setConfirmProd(false); persist({ ...cfg, mode: 'production' }); };

  const registerWebhook = async () => {
    setRegisteringWh(true);
    try {
      const callback_url = `${process.env.REACT_APP_BACKEND_URL}/api/notifications/whatsapp/webhook`;
      const { data } = await axios.post(`${API}/notifications/whatsapp/webhook/register`, { callback_url }, authHeader());
      if (data.success) { toast.success('Webhook entrant configuré ✅'); await load(); }
      else toast.error('Échec configuration webhook');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec configuration webhook'); }
    setRegisteringWh(false);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-green-600" /></div>;

  const isProd = cfg.mode === 'production';
  const connected = session?.connected;

  return (
    <div className="space-y-6" data-testid="openwa-config-panel">
      {/* Bandeau mode */}
      <div className={`rounded-xl p-4 flex items-center justify-between ${isProd ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center gap-3">
          {isProd ? <Rocket className="w-5 h-5 text-red-600" /> : <Beaker className="w-5 h-5 text-amber-600" />}
          <div>
            <p className={`font-semibold ${isProd ? 'text-red-700' : 'text-amber-700'}`}>
              {isProd ? 'MODE PRODUCTION — envois réels actifs' : 'MODE DEV — envois limités aux numéros de test'}
            </p>
            <p className="text-xs text-gray-600">
              {isProd ? 'Tous les clients opt-in reçoivent les notifications WhatsApp.' : 'Aucun message ne peut atteindre un numéro client réel.'}
            </p>
          </div>
        </div>
        <Badge className={connected ? 'bg-green-600' : 'bg-gray-400'} data-testid="openwa-session-badge">
          <Smartphone className="w-3 h-3 mr-1" />{session?.status || 'inconnu'}
        </Badge>
      </div>

      {/* Activation + moteur */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-600" />Passerelle WhatsApp (OpenWA)</CardTitle>
          <CardDescription>Notifications WhatsApp self-hosted — de l'inscription à la gestion d'opération.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div><Label>Activer la passerelle</Label><p className="text-xs text-gray-500">Active le canal WhatsApp OpenWA (moteur).</p></div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="openwa-enabled-switch" />
          </div>
          <Separator />

          {/* Mode + stratégie */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <div className="flex gap-2 mt-1">
                <Button type="button" variant={!isProd ? 'default' : 'outline'} className={!isProd ? 'bg-amber-600 hover:bg-amber-700' : ''} onClick={() => setCfg({ ...cfg, mode: 'dev' })} data-testid="openwa-mode-dev"><Beaker className="w-4 h-4 mr-1" />Dev</Button>
                <Button type="button" variant={isProd ? 'default' : 'outline'} className={isProd ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setConfirmProd(true)} data-testid="openwa-mode-prod"><Rocket className="w-4 h-4 mr-1" />Production</Button>
              </div>
            </div>
            <div>
              <Label>Stratégie en mode Dev</Label>
              <select className="w-full border rounded-md h-10 px-3 mt-1 text-sm" value={cfg.dev_strategy} onChange={(e) => setCfg({ ...cfg, dev_strategy: e.target.value })} data-testid="openwa-dev-strategy">
                <option value="redirect">Rediriger vers le numéro de test (tester le bout-en-bout)</option>
                <option value="block">Bloquer (rien n'est envoyé hors liste)</option>
              </select>
            </div>
          </div>

          {/* Numéros de test */}
          <div>
            <Label className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" />Numéros de test autorisés</Label>
            <p className="text-xs text-gray-500 mb-2">Seuls ces numéros reçoivent des messages en mode Dev. Format international sans +, ex. 2250500163737.</p>
            <div className="flex gap-2 mb-2">
              <Input placeholder="2250500163737" value={newNumber} onChange={(e) => setNewNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNumber()} data-testid="openwa-new-number-input" />
              <Button type="button" onClick={addNumber} data-testid="openwa-add-number-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {cfg.test_numbers.length === 0 && <span className="text-xs text-amber-600">Aucun numéro de test — requis en mode Dev.</span>}
              {cfg.test_numbers.map((n) => (
                <Badge key={n} variant="secondary" className="gap-1 py-1" data-testid={`openwa-test-number-${n}`}>
                  {n}<button onClick={() => removeNumber(n)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connexion gateway */}
      <Card>
        <CardHeader><CardTitle className="text-base">Connexion à la passerelle</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>URL de la passerelle</Label><Input value={cfg.base_url} onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })} placeholder="https://..." data-testid="openwa-base-url" /></div>
            <div><Label>Clé API</Label>
              <div className="relative">
                <Input type={showKey ? 'text' : 'password'} value={cfg.api_key} onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })} placeholder="owa_k1_..." data-testid="openwa-api-key" />
                <button type="button" className="absolute right-2 top-2.5 text-gray-400" onClick={() => setShowKey(!showKey)}>{showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <div><Label>Session ID</Label><Input value={cfg.session_id} onChange={(e) => setCfg({ ...cfg, session_id: e.target.value })} data-testid="openwa-session-id" /></div>
            <div><Label>Nom de session</Label><Input value={cfg.session_name} onChange={(e) => setCfg({ ...cfg, session_name: e.target.value })} data-testid="openwa-session-name" /></div>
            <div><Label>Numéro expéditeur</Label><Input value={cfg.sender_number} onChange={(e) => setCfg({ ...cfg, sender_number: e.target.value })} data-testid="openwa-sender" /></div>
            <div><Label>Secret Webhook (HMAC)</Label>
              <div className="relative">
                <Input type={showSecret ? 'text' : 'password'} value={cfg.webhook_secret} onChange={(e) => setCfg({ ...cfg, webhook_secret: e.target.value })} data-testid="openwa-webhook-secret" />
                <button type="button" className="absolute right-2 top-2.5 text-gray-400" onClick={() => setShowSecret(!showSecret)}>{showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadSession} data-testid="openwa-refresh-session"><RefreshCw className="w-4 h-4 mr-1" />Vérifier la session</Button>
            {session && <span className="text-sm text-gray-600">{connected ? <CheckCircle2 className="inline w-4 h-4 text-green-600 mr-1" /> : <AlertTriangle className="inline w-4 h-4 text-amber-600 mr-1" />}{session.phone || session.status} · envoyés aujourd'hui : {session.sent_today ?? 0}</span>}
          </div>
          <Separator />
          <div>
            <Label className="flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-600" />Webhook entrant (réponses clients)</Label>
            <p className="text-xs text-gray-500 mb-2">Active la réception des réponses WhatsApp (OUI/NON/NEGOCIER) et les accusés de réception. Configure automatiquement l'URL + le secret HMAC côté passerelle.</p>
            {cfg.webhook_url
              ? <p className="text-xs text-green-700 mb-2 break-all"><CheckCircle2 className="inline w-3 h-3 mr-1" />Configuré : {cfg.webhook_url}</p>
              : <p className="text-xs text-amber-600 mb-2">Non configuré — le bidirectionnel est inactif.</p>}
            <Button variant="outline" size="sm" onClick={registerWebhook} disabled={registeringWh} data-testid="openwa-register-webhook-btn">
              {registeringWh ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Activity className="w-4 h-4 mr-1" />}Configurer le webhook entrant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Anti-ban */}
      <Card>
        <CardHeader><CardTitle className="text-base">Paramètres anti-ban</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div><Label>Max / heure</Label><Input type="number" value={cfg.rate.per_hour} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, per_hour: +e.target.value } })} data-testid="openwa-rate-perhour" /></div>
          <div><Label>Délai min (s)</Label><Input type="number" value={cfg.rate.min_delay_s} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, min_delay_s: +e.target.value } })} /></div>
          <div><Label>Délai max (s)</Label><Input type="number" value={cfg.rate.max_delay_s} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, max_delay_s: +e.target.value } })} /></div>
          <div><Label>Fenêtre début (h)</Label><Input type="number" value={cfg.rate.window_start_h} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, window_start_h: +e.target.value } })} /></div>
          <div><Label>Fenêtre fin (h)</Label><Input type="number" value={cfg.rate.window_end_h} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, window_end_h: +e.target.value } })} /></div>
          <div><Label>Fuseau</Label><Input value={cfg.rate.tz} onChange={(e) => setCfg({ ...cfg, rate: { ...cfg.rate, tz: e.target.value } })} /></div>
        </CardContent>
      </Card>

      {/* Test */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4" />Envoyer un test</CardTitle>
          <CardDescription>En mode Dev, uniquement vers un numéro de la liste de test.</CardDescription></CardHeader>
        <CardContent className="flex gap-2">
          <Input placeholder="2250500163737" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="openwa-test-phone" />
          <Button onClick={sendTest} disabled={sendingTest} data-testid="openwa-send-test-btn">{sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Tester</Button>
        </CardContent>
      </Card>

      {/* Monitoring outbox */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" />File d'envoi (outbox)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(outbox.counts || {}).map(([k, v]) => (
              <Badge key={k} variant="outline" data-testid={`openwa-count-${k}`}>{k}: {v}</Badge>
            ))}
            <Button variant="ghost" size="sm" onClick={loadOutbox}><RefreshCw className="w-3 h-3 mr-1" />Rafraîchir</Button>
          </div>
          <div className="max-h-64 overflow-auto text-xs border rounded-md divide-y">
            {(outbox.items || []).slice(0, 20).map((it) => (
              <div key={it.id} className="p-2 flex items-center justify-between gap-2">
                <span className="truncate flex-1">{it.template_key || it.type} → {it.effective_to}</span>
                <Badge className={it.status === 'sent' ? 'bg-green-600' : it.status === 'failed' ? 'bg-red-600' : it.status?.startsWith('skipped') ? 'bg-gray-400' : 'bg-amber-500'}>{it.status}</Badge>
              </div>
            ))}
            {(!outbox.items || outbox.items.length === 0) && <div className="p-3 text-gray-400">Aucun message.</div>}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="openwa-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Enregistrer
        </Button>
      </div>

      <AlertDialog open={confirmProd} onOpenChange={setConfirmProd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-red-600" />Activer le MODE PRODUCTION ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous allez activer les envois WhatsApp <strong>RÉELS vers tous les clients opt-in</strong>.
              Les notifications ne seront plus limitées aux numéros de test. Confirmez-vous ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="openwa-prod-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={switchToProduction} data-testid="openwa-prod-confirm">Activer Production</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
